import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { api, formatApiErrorDetail } from "../lib/api";
import { SERVICES } from "../data/services";
import {
  LayoutDashboard, Users, DollarSign, Clock, CheckCircle2, XCircle, LogOut, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_FLOW = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "pending_review", label: "Pending review" },
  { id: "confirmed", label: "Confirmed" },
  { id: "completed", label: "Completed" },
  { id: "rejected", label: "Rejected" },
];

const STATUS_COLORS = {
  new: "bg-[#D4AF37]/15 text-[#8A6F1A] border-[#D4AF37]/30",
  pending_review: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings", { params: filter === "all" ? {} : { status: filter } }),
      ]);
      setStats(s.data);
      setBookings(b.data.bookings || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user !== false) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, user]);

  if (authLoading) return <div className="p-10 text-sm text-[#64748B]">Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const updateBooking = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, patch);
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      if (selected && selected.id === id) setSelected(data);
      toast.success("Booking updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-2">Admin dashboard</div>
            <h1 className="font-display text-3xl md:text-4xl text-[#0A1128] tracking-tight">
              PRIMEGLOBE Operations
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              Signed in as <strong className="text-[#0A1128]">{user?.email}</strong>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-btn" onClick={load} className="btn-outline-navy inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm">
              <RefreshCw size={14} /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/admin/login"); }}
              className="btn-outline-navy inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-8">
          <StatCard icon={LayoutDashboard} label="Total" value={stats?.total ?? 0} testid="stat-total" />
          <StatCard icon={Clock} label="New" value={stats?.new ?? 0} testid="stat-new" />
          <StatCard icon={Clock} label="Pending" value={stats?.pending_review ?? 0} testid="stat-pending" />
          <StatCard icon={CheckCircle2} label="Confirmed" value={stats?.confirmed ?? 0} testid="stat-confirmed" />
          <StatCard icon={Users} label="Completed" value={stats?.completed ?? 0} testid="stat-completed" />
          <StatCard icon={DollarSign} label="Revenue" value={`$${Number(stats?.revenue || 0).toFixed(0)}`} testid="stat-revenue" />
        </div>

        {/* Filter */}
        <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="admin-filter-tabs">
          {STATUS_FLOW.map((s) => (
            <button
              key={s.id}
              data-testid={`admin-filter-${s.id}`}
              onClick={() => setFilter(s.id)}
              className={`px-4 py-2 rounded-sm text-sm border transition ${
                filter === s.id ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0A1128]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-6 bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
          <div className="overflow-x-auto scroll-thin">
            <table className="w-full text-sm" data-testid="admin-bookings-table">
              <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase tracking-[0.15em]">
                <tr>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Service</th>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Total</th>
                  <th className="text-left px-4 py-3">Deposit</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-[#64748B]">Loading…</td></tr>
                )}
                {!loading && bookings.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-[#64748B]">No bookings yet.</td></tr>
                )}
                {bookings.map((b) => {
                  const svc = SERVICES.find((s) => s.id === b.service);
                  return (
                    <tr key={b.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`admin-booking-row-${b.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#0A1128]">{b.contact?.name}</div>
                        <div className="text-xs text-[#64748B]">{b.contact?.phone} · {b.contact?.borough || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-[#0A1128]">{svc?.label || b.service}</td>
                      <td className="px-4 py-3 text-[#475569]">{b.preferred_date}<br/><span className="text-xs">{b.preferred_time}</span></td>
                      <td className="px-4 py-3 font-medium text-[#0A1128]">${Number(b.final_price || b.pricing?.total || 0).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs ${b.deposit_paid ? "text-emerald-700" : "text-[#64748B]"}`}>
                          {b.deposit_paid ? "Paid" : `Owed $${Number(b.pricing?.deposit || 0).toFixed(2)}`}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 rounded-sm border text-xs capitalize ${STATUS_COLORS[b.status] || ""}`}>
                          {String(b.status).replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`admin-view-${b.id}`}
                          onClick={() => setSelected(b)}
                          className="text-xs font-medium text-[#D4AF37] hover:underline"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && (
        <BookingDrawer
          booking={selected}
          onClose={() => setSelected(null)}
          onUpdate={(patch) => updateBooking(selected.id, patch)}
        />
      )}

      <SiteFooter />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-[0.16em]">
        <Icon size={14} className="text-[#D4AF37]" /> {label}
      </div>
      <div className="font-display text-2xl text-[#0A1128] mt-2">{value}</div>
    </div>
  );
}

function BookingDrawer({ booking, onClose, onUpdate }) {
  const svc = SERVICES.find((s) => s.id === booking.service);
  const [finalPrice, setFinalPrice] = useState(booking.final_price ?? booking.pricing?.total ?? 0);
  const [notes, setNotes] = useState(booking.admin_notes || "");

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-booking-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto scroll-thin border-l border-[#E2E8F0]">
        <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="eyebrow">Booking</div>
            <div className="font-display text-lg text-[#0A1128]">{svc?.label || booking.service}</div>
          </div>
          <button data-testid="admin-drawer-close" onClick={onClose} className="text-[#64748B] hover:text-[#0A1128]">
            <XCircle size={22} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <Section title="Customer">
            <KV k="Name" v={booking.contact?.name} />
            <KV k="Email" v={booking.contact?.email} />
            <KV k="Phone" v={booking.contact?.phone} />
            <KV k="Address" v={booking.contact?.address} />
            <KV k="Borough" v={booking.contact?.borough} />
            <KV k="Notes" v={booking.contact?.notes} />
          </Section>

          <Section title="Service details">
            <KV k="Condition" v={booking.condition} />
            <KV k="Floors" v={booking.floors} />
            <KV k="Urgency" v={String(booking.urgency).replace(/_/g, " ")} />
            <KV k="Add-ons" v={booking.addons?.join(", ") || "None"} />
            <KV k="When" v={`${booking.preferred_date} @ ${booking.preferred_time}`} />
            <KV k="Details" v={JSON.stringify(booking.details || {})} />
          </Section>

          <Section title="Pricing">
            <KV k="Base" v={`$${Number(booking.pricing?.base_price || 0).toFixed(2)}`} />
            <KV k="Add-ons" v={`$${Number(booking.pricing?.addon_total || 0).toFixed(2)}`} />
            <KV k="Subtotal" v={`$${Number(booking.pricing?.subtotal || 0).toFixed(2)}`} />
            <KV k="Total estimate" v={`$${Number(booking.pricing?.total || 0).toFixed(2)}`} />
            <KV k="Deposit (50%)" v={`$${Number(booking.pricing?.deposit || 0).toFixed(2)}`} />
            <KV k="Workers" v={booking.pricing?.estimated_workers} />
            <KV k="Hours" v={`${booking.pricing?.estimated_hours_min}–${booking.pricing?.estimated_hours_max}`} />
          </Section>

          {booking.photos?.length > 0 && (
            <Section title={`Photos (${booking.photos.length})`}>
              <div className="grid grid-cols-3 gap-2 col-span-2">
                {booking.photos.map((p, i) => (
                  <img key={i} src={p} alt={`photo-${i}`} className="w-full h-24 object-cover rounded-sm border border-[#E2E8F0]" />
                ))}
              </div>
            </Section>
          )}

          <div className="border-t border-[#E2E8F0] pt-6 space-y-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-[0.15em] text-[#64748B] mb-2">Final price (editable)</div>
              <div className="flex gap-2">
                <input type="number" min={0} step="0.01" className="pg-input"
                  data-testid="admin-final-price-input"
                  value={finalPrice}
                  onChange={(e) => setFinalPrice(Number(e.target.value))} />
                <button data-testid="admin-save-price" onClick={() => onUpdate({ final_price: finalPrice })}
                  className="btn-navy px-4 py-2 rounded-sm text-sm">Save</button>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-[0.15em] text-[#64748B] mb-2">Admin notes</div>
              <textarea className="pg-input min-h-[80px]" data-testid="admin-notes-input"
                value={notes} onChange={(e) => setNotes(e.target.value)} />
              <button data-testid="admin-save-notes"
                onClick={() => onUpdate({ admin_notes: notes })}
                className="mt-2 btn-outline-navy px-4 py-2 rounded-sm text-xs">Save notes</button>
            </div>

            <div className="flex items-center justify-between bg-[#F8FAFC] p-4 rounded-sm">
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#64748B]">Deposit</div>
                <div className="text-sm text-[#0A1128] mt-1">{booking.deposit_paid ? "Paid" : "Not paid"}</div>
              </div>
              <button
                data-testid="admin-toggle-deposit"
                onClick={() => onUpdate({ deposit_paid: !booking.deposit_paid })}
                className="btn-outline-navy px-4 py-2 rounded-sm text-xs"
              >
                Mark {booking.deposit_paid ? "unpaid" : "paid"}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button data-testid="admin-approve-btn" onClick={() => onUpdate({ status: "confirmed" })}
                className="btn-gold py-3 rounded-sm text-sm inline-flex items-center justify-center gap-2">
                <CheckCircle2 size={16} /> Approve / Confirm
              </button>
              <button data-testid="admin-reject-btn" onClick={() => onUpdate({ status: "rejected" })}
                className="btn-outline-navy py-3 rounded-sm text-sm inline-flex items-center justify-center gap-2">
                <XCircle size={16} /> Reject
              </button>
              <button data-testid="admin-complete-btn" onClick={() => onUpdate({ status: "completed" })}
                className="col-span-2 btn-navy py-3 rounded-sm text-sm">
                Mark as Completed
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="eyebrow mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-4 text-sm">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-xs text-[#94A3B8] uppercase tracking-[0.14em]">{k}</div>
      <div className="text-[#0A1128] mt-1 break-words">{v || "—"}</div>
    </div>
  );
}
